import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Calendar, Clock, MapPin, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Events = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await axios.get(`${API}/events`);
        setEvents(response.data);
      } catch (error) {
        console.error('Error fetching events:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, []);

  const getEventTypeColor = (type) => {
    const colors = {
      Cultural: '#53a5ca',
      Service: '#e37c55',
      Social: '#dbbf57',
      Conference: '#7e2f22'
    };
    return colors[type] || '#53a5ca';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long',
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const sortedEvents = [...events].sort((first, second) => new Date(first.date) - new Date(second.date));
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingEvents = sortedEvents.filter((event) => new Date(event.dateEnd || event.date) >= today);
  const pastEvents = sortedEvents.filter((event) => new Date(event.dateEnd || event.date) < today).reverse();

  const renderEventCard = (event) => (
    <Card key={event.id} className="overflow-hidden border-0 shadow-lg hover:shadow-xl transition-all duration-300" data-testid={`event-card-${event.id}`}>
      <div className="flex flex-col md:flex-row">
        <div className="w-full md:w-48 p-6 md:p-8 flex flex-col items-center justify-center text-center" style={{ backgroundColor: `${getEventTypeColor(event.type)}20` }}>
          <Calendar className="h-12 w-12 mb-3" style={{ color: getEventTypeColor(event.type) }} />
          <div className="text-3xl font-bold" style={{ color: getEventTypeColor(event.type) }}>{new Date(event.date).getDate()}</div>
          <div className="text-sm font-semibold mt-1" style={{ color: getEventTypeColor(event.type) }}>
            {new Date(event.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
          </div>
        </div>
        <div className="flex-1 p-6">
          <div className="flex items-start justify-between mb-3">
            <h3 className="text-2xl font-bold" style={{ color: '#7e2f22' }}>{event.title}</h3>
            <Badge className="text-xs font-semibold ml-4" style={{ backgroundColor: getEventTypeColor(event.type), color: 'white' }}>{event.type}</Badge>
          </div>
          <p className="text-gray-600 mb-4 leading-relaxed">{event.description}</p>
          <div className="space-y-2 text-sm text-gray-600">
            <div className="flex items-center space-x-2"><Calendar className="h-4 w-4" style={{ color: '#53a5ca' }} /><span>{formatDate(event.date)}</span></div>
            <div className="flex items-center space-x-2"><Clock className="h-4 w-4" style={{ color: '#53a5ca' }} /><span>{event.time}</span></div>
            <div className="flex items-start space-x-2"><MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#53a5ca' }} /><span>{event.location}</span></div>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-16 md:py-24" style={{ backgroundColor: '#e37c5520' }}>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#7e2f22' }}>
              Events
            </h1>
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              Join us for cultural gatherings, community service projects, and leadership development opportunities — all shown in chronological order with past events preserved below.
            </p>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Loading events...</p>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-12">
              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#7e2f22' }}>Upcoming Events</h2>
                  <p className="text-gray-600">These events are ordered from nearest to farthest.</p>
                </div>
                <div className="space-y-6">
                  {upcomingEvents.length ? upcomingEvents.map(renderEventCard) : <p className="text-center text-gray-500">No upcoming events at the moment.</p>}
                </div>
              </div>

              <div className="space-y-6">
                <div className="text-center">
                  <h2 className="text-3xl md:text-4xl font-bold mb-3" style={{ color: '#7e2f22' }}>Past Events</h2>
                  <p className="text-gray-600">Past events stay visible here once their dates have passed.</p>
                </div>
                <div className="space-y-6">
                  {pastEvents.length ? pastEvents.map(renderEventCard) : <p className="text-center text-gray-500">No past events yet.</p>}
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#7e2f22' }}>
              Don't Miss Out!
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Join Naiwa Daughters to participate in all our events and be part of a growing community of Indigenous young women leaders.
            </p>
            <Link to="/join">
              <Button 
                size="lg" 
                className="text-white hover:opacity-90 transition-all duration-200"
                style={{ backgroundColor: '#53a5ca' }}
              >
                Join Naiwa Daughters
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Events;
